<!--
  This component creates a grid space for tiles to be inserted that have the capability to be dragged and hidden.
  The parent component will specify which tiles will be added to the grid via the tilesProp prop. The storeModule
  prop will be used to track all changes to the grid.

  To setup this component properly:
    - a tileLayout store module with a unique name must be added to our vuex stores modules in index.js
    - To properly interact with the top nav bar tile control, the route for the page must contain a
      the meta field: draggabeGridStoreModule, which contains the name of the tileLyout module specified 
      in our vuex store's index.js file. 
    - If the component does not retrieve and render its own data, for the componentProp property that is
      the realtime data, use DraggableTileDashboard's updateTileComponentProp function

-->
<template>
  <div v-if="doneLoadingTiles"><!-- :autoSize="true" -->
    <!--Stats tiles-->
    <grid-layout
      :layout.sync="tiles"
      :cols="columnsByBreakpoint"
      :rowHeight="rowHeight"
      :breakpoints="breakpoints"
      :key="gridComponentKey"
      :margin=[10,10]
      :responsive="true"
      :isDraggable="true"
      :isResizable="false"
      :vertical-compact="true"
      :autoSize="false"
      @move-event="tileDragEvent"
      @breakpoint-changed="breakpointChangedEvent"
      @layout-updated="saveLayout"
    >
      <grid-item
        v-for="item in tiles"
        :x="item.x"
        :y="item.y"
        :h="item.h"
        :w="item.w"
        :i="item.i"
        :key="item.i"
        @move="tileDragEvent"
        
      >
        <el-card v-show="item.hidden === false" class="tile" v-on:click.native="openTileModal(item)">
          <p-button
            class="deleteTileButton"
            type="default"
            icon
            round
            v-on:click.stop="removeCardButtonPressed(item)"
          >x</p-button>
          <template>
            <component :is="item.component" v-bind="item.componentProps">
              <template v-slot:[slotName] v-for="(slotValue, slotName) in item.slots">
                <div :key="slotName">
                  {{slotValue}}
                </div>
              </template>
            </component>
          </template>
        </el-card>
      </grid-item>
    </grid-layout>
    <div v-for="item in tiles" :key="item.name">
      <div v-if="item.modal">
        <Dialog 
          :visible.sync="modalControls[item.name]"
          :width="item.modal.width"
          :fullscreen="item.modal.fullscreen"
          :center='true'
          top="5vh"
          >
          <div v-if="item.modal.modalComponent">
            <component :is="item.modal.modalComponent" v-bind="item.modal.modalComponentProps">
              <template v-slot:[slotName] v-for="(slotValue, slotName) in item.modal.modalComponentSlots">
                <div :key="slotName">
                  {{slotValue}}
                </div>
              </template>
            </component>
          </div>
          <template v-slot:[slotName] v-for="(slotValue, slotName) in item.modal.modalSlots">
            <div :key="slotName">
              {{slotValue}}
            </div>
          </template>
        </Dialog>
      </div>
    </div>
  </div>
</template>
<script>
import VueGridLayout from "vue-grid-layout";
import {Dialog} from 'element-ui'

export default {
  components: {
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    Dialog,
  },

  props: {
    tilesProp: {
      type: Array,
      required: true,
      description: `An array of tile objects. Each tile object will be of the form:
            {
                name: the human readable name of the tile
                h: the height of the tile in number of rows
                w: the width of the tile in number of columns
                component: The component rendered on the tile
                componentProps: The props needed to populate the component.
                slots: A list of slots to populate where the property name is the slot key and the value will be used in the slot
                componentPath: The path to the component, relative to the src directory. (Do not include src or @ before path)
                modal: { The modal property is to be included when you wish to specify the modal that will be launched when the tile is clicked
                  modalComponent: The component that the modal will contain
                  modalComponentProps: Props for the modal componenet
                  modalComponentSlots: Same as slots but for the component in the modal
                  modalWidth: width of the modal (do not include for auto adjustment based on interior component)
                  modalFullscreen: whether or not to have the modal be full screen (do not include if you dont want it to be fullscreen)
                  modalSlots: The same as the other slots property but for the modal, have options of title and footer
                  modalComponentPath: The path to the modal component, relative to the src directory
                }
            }`
    },
    storeModule: {
      type: String,
      required: true,
      default: "none",
      description: `The string representation of the module where the tile layout will be saved. 
        Use this only if you want to save the state of the layout for the user`,
    },
    version: {
      type: String,
      required: false,
      defailt: '0.0',
      description: `The version number fot he tilessProp or system. Whenever anything changes in the tilesProp or if this system changes, change the version 
        number and the users local storage of the draggable grid will be cleared allowing for the proper funcitoning of updates`
    }
  },

  computed: {
    tiles: {
      get() {
        return this.$store.getters[`${this.storeModule}/getLayout`]
      },
      set(layout) {
        return this.saveLayout(layout)
      }
    },
    rowHeight() {
      return this.$store.getters[`${this.storeModule}/getRowHeight`]
    },
    columnsByBreakpoint() {
      return this.$store.getters[`${this.storeModule}/getColumnsByBreakpoint`]
    },
    breakpoints() {
      return this.$store.getters[`${this.storeModule}/getBreakpoints`]
    }
  },
  watch:{
    tilesProp: {
      handler(value) {
        this.loadTileComponents();
        this.$store.dispatch(`${this.storeModule}/syncUpdatedInitialTileData`, value)
        return this.tilesProp
      },
      deep: true,
      immediate: true
    },
  },
  data() {
    return {
      modalControls: {
      },
      isDragging: false,
      gridComponentKey: 0,
      doneLoadingTiles: false
    }
  },
  methods: {
    removeCardButtonPressed(tile) {
      tile.hidden = true
      this.$store.commit(`${this.storeModule}/saveTileHiddenState`, tile)
    },
    updateTileComponentProps(tileName, propertyName, propertyValue) {
      var parameters = {
          name: tileName,
          propertyName: propertyName,
          propertyValue: propertyValue
        }
      this.$store.dispatch(`${this.storeModule}/updateTileComponentProp`, parameters)
    },
    openTileModal(tile) {
      if(!this.isDragging) {
        this.modalControls[tile.name] = true
      }
      this.isDragging = false
      // Also record patient interaction
      if(this.$route.name == 'User Dashboard') {
        this.$store.dispatch('patientInteractionTimer/recordAction', 'tileClick')
      }
      if (tile.clickAction != null) {
        tile.onClickAction()
      }
    },
    tileDragEvent() {
      this.isDragging = true
    },
    saveLayout(layout) {
      this.$store.commit(`${this.storeModule}/saveLayout`, layout);
    },
    breakpointChangedEvent: function(newBreakpoint, newLayout){
      this.$store.commit(`${this.storeModule}/setBreakpoint`, newBreakpoint)
    },
    forceUpdateGridComponent() {
      this.gridComponentKey += 1
    },
    loadTileComponents(){
      for(var tile of this.tilesProp) {
        // dynamically import the needed components
        let componentPath = tile.componentPath
        let componentName = tile.component
        this.$options.components[componentName] = () => import(`../../${componentPath}.vue`)
        // if this tile contains modal information
        if(tile.modal) {
          if (tile.modal.modalComponent) {
            // dynamically import the modal componenets
            let modalComponentPath = tile.modal.modalComponentPath
            let modalComponentName = tile.modal.modalComponent
            this.$options.components[modalComponentName] = () => import(`../../${modalComponentPath}.vue`)
          }
          // create the modal controls
            this.$set(this.modalControls, tile.name, false)
        }
      }
    },
    checkTilePropForNewVersion() {
      if (this.version != this.$store.getters[`${this.storeModule}/getVersion`]) {
        this.$store.commit(`${this.storeModule}/setVersion`, this.version);
        this.$store.dispatch(`${this.storeModule}/resetLayoutToInitialState`, this.tilesProp)
      }
    },
  },
  async created() {
    this.loadTileComponents();
    this.checkTilePropForNewVersion()
    this.$store.dispatch(`${this.storeModule}/syncUpdatedInitialTileData`, this.tilesProp)
  },
  async beforeMount() { 
  },
  mounted() {
    this.forceUpdateGridComponent()
    this.doneLoadingTiles = true
  },
};
</script>
<style>
.deleteTileButton {
  transform: scale(0.3);
  opacity: 0.15;
  z-index: 10;
  position: absolute !important;
  right: -10px;
  top: -18px;
}
.deleteTileButton:hover {
  opacity: 1;
}

.tile {
  height: 100% !important;
  cursor: default;
}
</style>
